import { roll2D6 } from '../utils/dice.js';
import { getYear } from '../utils/time.js';

/**
 * Resolves a raid against the settlement
 * @param {Object} params - Parameters object
 * @param {number} params.day - Current game day
 * @param {Object} params.rolls - Task roll results
 * @param {Object} params.pushes - Push results
 * @param {Array} params.buildings - Completed buildings
 * @param {Object} params.resources - Current resources { food, wood, stone }
 * @param {Array} params.workers - Current workers
 * @returns {Object} Combat result with raid strength, defense, outcome, and updated resources/workers
 */
export function resolveRaid({ day, rolls = {}, pushes = {}, buildings = [], resources, workers }) {
  const year = getYear(day);
  const raidStrength = roll2D6() + (year * 2);

  // Calculate defense
  let defense = 0;
  if (rolls.guard) {
    defense = rolls.guard.rolls.reduce((a, b) => a + b, 0);
    if (pushes.guard?.success) {
      defense += pushes.guard.value;
    }
  }

  // Add wall bonus
  const hasWall = buildings.some(b => b.type === 'wall' && b.complete);
  if (hasWall) {
    defense += 3;
  }

  const won = defense >= raidStrength;
  const damage = won ? 0 : raidStrength - defense;
  const surplus = won ? defense - raidStrength : 0; // For conquest ending

  // Calculate losses and updated state
  const { losses, newResources, newWorkers } = won
    ? {
        losses: { food: 0, wood: 0, stone: 0, workerInjured: false },
        newResources: resources,
        newWorkers: workers
      }
    : distributeDamage(damage, resources, workers);

  return {
    raidStrength,
    defense,
    won,
    damage,
    surplus,
    losses,
    resources: newResources,
    workers: newWorkers
  };
}

/**
 * Distributes raid damage across resources and workers
 * @param {number} damage - Total damage to distribute
 * @param {Object} resources - Current resources
 * @param {Array} workers - Current workers
 * @returns {Object} { losses, newResources, newWorkers }
 */
function distributeDamage(damage, resources, workers) {
  let remaining = damage;
  const losses = { food: 0, wood: 0, stone: 0, workerInjured: false };
  let newFood = resources.food;
  let newWood = resources.wood;
  let newStone = resources.stone;
  let newWorkers = [...workers];

  // Deduct from food first
  const foodLoss = Math.min(remaining, newFood);
  newFood -= foodLoss;
  remaining -= foodLoss;
  losses.food = foodLoss;

  // Then wood
  if (remaining > 0) {
    const woodLoss = Math.min(remaining, newWood);
    newWood -= woodLoss;
    remaining -= woodLoss;
    losses.wood = woodLoss;
  }

  // Then stone
  if (remaining > 0) {
    const stoneLoss = Math.min(remaining, newStone);
    newStone -= stoneLoss;
    remaining -= stoneLoss;
    losses.stone = stoneLoss;
  }

  // If still damage remaining, injure workers
  if (remaining > 0) {
    const healthyWorker = newWorkers.find(w => w.status === 'healthy');
    if (healthyWorker) {
      newWorkers = newWorkers.map(w =>
        w.id === healthyWorker.id ? { ...w, status: 'injured' } : w
      );
      losses.workerInjured = true;
    } else {
      const injuredWorker = newWorkers.find(w => w.status === 'injured');
      if (injuredWorker) {
        newWorkers = newWorkers.map(w =>
          w.id === injuredWorker.id ? { ...w, status: 'dead' } : w
        );
        losses.workerInjured = true;
      }
    }
  }

  return {
    losses,
    newResources: { food: newFood, wood: newWood, stone: newStone },
    newWorkers
  };
}
